import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PortalShell } from "@/components/PortalShell";
import api, { formatApiError } from "@/lib/api";
import { Calendar, Clock, Mail, Check, X, Save, Download, Copy } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [booking, setBooking] = useState({ lead_time_months: 3, slots: [], min_date: "" });
  const [slotsText, setSlotsText] = useState("");
  const [savingBooking, setSavingBooking] = useState(false);
  const [launch, setLaunch] = useState(null);
  const [reservations, setReservations] = useState(null);
  const [drip, setDrip] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [funnelDays, setFunnelDays] = useState(30);
  const [leads, setLeads] = useState(null);
  const [announcing, setAnnouncing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const copyReply = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  const loadAll = async () => {
    try {
      const [s, a, p, m, b, l, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/appointments"),
        api.get("/admin/partner-applications"),
        api.get("/admin/messages"),
        api.get("/admin/booking-settings"),
        api.get("/launch-status"),
        api.get("/admin/reservations"),
      ]);
      setStats(s.data);
      setAppointments(a.data);
      setApplications(p.data);
      setMessages(m.data);
      setBooking(b.data);
      setSlotsText((b.data.slots || []).join(", "));
      setLaunch(l.data);
      setReservations(r.data);
      try {
        const d = await api.get("/admin/drip");
        setDrip(d.data);
      } catch { /* non-critical */ }
      try {
        const f = await api.get("/admin/analytics/funnel", { params: { days: funnelDays } });
        setFunnel(f.data);
      } catch { /* non-critical */ }
      try {
        const l = await api.get("/admin/leads");
        setLeads(l.data);
      } catch { /* non-critical */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportReservations = async (product) => {
    try {
      const res = await api.get("/admin/reservations/export", { params: { product }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${product}-reservations.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendAnnouncement = async () => {
    if (!window.confirm("Send the 'we're live' launch email to ALL BArecord + SecureDesk reservers now? This normally sends automatically on Nov 7.")) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post("/admin/launch/announce", { force: true });
      toast.success(data.skipped ? "Already announced." : `Launch email sent to ${data.sent} reservers.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAnnouncing(false);
    }
  };

  const toggleDrip = async (enabled) => {
    try {
      const { data } = await api.put("/admin/drip", { enabled });
      setDrip((d) => ({ ...(d || {}), enabled: data.enabled }));
      toast.success(`Pre-launch drip ${data.enabled ? "enabled" : "paused"}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.sent ? "Weekly digest emailed to you." : "Digest not sent (no notification email set).");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateLaunch = async (override) => {
    try {
      const { data } = await api.put("/admin/launch", { override });
      setLaunch(data);
      toast.success(`Launch access set to "${override}".`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadFunnel = async (days) => {
    setFunnelDays(days);
    try {
      const f = await api.get("/admin/analytics/funnel", { params: { days } });
      setFunnel(f.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/partner-applications/${id}`, { status });
      toast.success(`Application ${status}.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setApptStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Consultation ${status}.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveBooking = async (e) => {
    e.preventDefault();
    setSavingBooking(true);
    try {
      const slots = slotsText.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/admin/booking-settings", {
        lead_time_months: Number(booking.lead_time_months),
        slots,
      });
      setBooking(data);
      setSlotsText((data.slots || []).join(", "));
      toast.success("Booking calendar updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingBooking(false);
    }
  };

  const statItems = stats
    ? [
        { label: "Clients", value: stats.clients },
        { label: "Partners", value: stats.partners },
        { label: "Consults", value: stats.appointments, sub: `${stats.appointments_active} active` },
        { label: "Applications", value: stats.applications, sub: `${stats.applications_pending} pending` },
        { label: "Messages", value: stats.messages },
      ]
    : [];

  return (
    <PortalShell
      eyebrow="Admin Dashboard"
      title="Operations overview"
      subtitle="Monitor consultations, review partner applications, and read inbound messages."
    >
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden mb-12" data-testid="admin-stats">
        {statItems.map((s) => (
          <div key={s.label} className="bg-surface p-6">
            <div className="font-display text-3xl lg:text-4xl tracking-tighter text-paper">{s.value}</div>
            <div className="mt-2 text-xs uppercase tracking-widest text-fog">{s.label}</div>
            {s.sub && <div className="mt-1 text-xs text-fog/70">{s.sub}</div>}
          </div>
        ))}
      </div>

      <Tabs defaultValue="applications" className="w-full">
        <TabsList className="bg-surface border border-white/10 mb-8" data-testid="admin-tabs">
          <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Consultations</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="booking" data-testid="tab-booking">Calendar</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="tab-reservations">Reservations</TabsTrigger>
          <TabsTrigger value="launch" data-testid="tab-launch">Launch</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="tab-funnel">Funnel</TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          {!leads || (leads.exposure.length === 0 && leads.baseline.length === 0) ? (
            <Empty text="No leads yet — exposure checks and guest baseline bookings will appear here." />
          ) : (
            <div className="space-y-10" data-testid="leads-panel">
              <div>
                <h3 className="font-display text-xl tracking-tight text-paper mb-1">Baseline booking requests</h3>
                <p className="text-xs text-fog mb-5">Guest bookings for the $499 Baseline Review — confirm by email within 1 business day.</p>
                {leads.baseline.length === 0 ? (
                  <p className="text-sm text-fog" data-testid="leads-baseline-empty">None yet.</p>
                ) : (
                  <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10" data-testid="leads-baseline-list">
                    {leads.baseline.map((b) => (
                      <div key={b.id} className="bg-black/40 p-5 sm:p-6" data-testid={`lead-baseline-${b.id}`}>
                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                          <span className="font-display text-lg tracking-tight text-paper">{b.name}</span>
                          <a href={`mailto:${b.email}`} className="text-sm text-fog hover:text-paper transition-colors">{b.email}</a>
                          {b.practice && <span className="text-sm text-fog">· {b.practice}</span>}
                          {b.preferred_date && <span className="text-xs uppercase tracking-widest text-paper">Prefers {b.preferred_date}</span>}
                          <span className="ml-auto text-xs text-fog/70">{(b.created_at || "").slice(0, 10)}</span>
                        </div>
                        {b.message && <p className="mt-2 text-sm text-fog whitespace-pre-line line-clamp-4">{b.message}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-display text-xl tracking-tight text-paper mb-1">Exposure-check leads</h3>
                <p className="text-xs text-fog mb-5">Domains checked with the free tool — rows with an email are warm leads who requested the summary.</p>
                {leads.exposure.length === 0 ? (
                  <p className="text-sm text-fog" data-testid="leads-exposure-empty">None yet.</p>
                ) : (
                  <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10" data-testid="leads-exposure-list">
                    {leads.exposure.map((x) => (
                      <div key={x.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-black/40 px-5 sm:px-6 py-4" data-testid={`lead-exposure-${x.id}`}>
                        <span className="font-mono text-sm text-paper">{x.domain}</span>
                        <span className={`text-xs ${x.passed >= 4 ? "text-green-400" : x.passed >= 2 ? "text-yellow-400" : "text-red-400"}`}>
                          {x.passed}/{x.total} passed
                        </span>
                        {x.email ? (
                          <a href={`mailto:${x.email}`} className="text-sm text-paper underline underline-offset-2 hover:text-fog transition-colors">
                            {x.name ? `${x.name} · ` : ""}{x.email}
                          </a>
                        ) : (
                          <span className="text-xs text-fog/60">anonymous</span>
                        )}
                        <span className="ml-auto text-xs text-fog/70">{(x.created_at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="funnel">
          {!funnel ? (
            <Empty text="No analytics yet — funnel data appears as visitors browse the site." />
          ) : (
            <div className="rounded-2xl border border-white/10 bg-surface p-8" data-testid="funnel-panel">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                  <h3 className="font-display text-xl tracking-tight text-paper">Conversion funnel</h3>
                  <p className="mt-1 text-xs text-fog">
                    {funnel.page_views} page views · last {funnel.days} days · visit → booking {funnel.overall_visit_to_booking}%
                  </p>
                </div>
                <div className="flex gap-2">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => loadFunnel(d)}
                      data-testid={`funnel-days-${d}`}
                      className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${
                        funnelDays === d ? "bg-paper text-black border-paper" : "border-white/15 text-fog hover:text-paper"
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-5">
                {funnel.stages.map((s, i) => {
                  const max = Math.max(...funnel.stages.map((x) => x.count), 1);
                  return (
                    <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span className="text-sm text-paper">{s.label}</span>
                        <span className="text-sm text-fog">
                          <span className="font-display text-lg text-paper mr-2">{s.count}</span>
                          {s.rate_from_prev !== null && (
                            <span className="text-xs">{s.rate_from_prev}% of previous</span>
                          )}
                        </span>
                      </div>
                      <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-paper transition-all duration-700"
                          style={{ width: `${Math.max((s.count / max) * 100, s.count > 0 ? 2 : 0)}%`, opacity: 1 - i * 0.12 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-8 text-xs text-fog/70 leading-relaxed">
                Visitors are counted by anonymous first-party session ID (no cookies beyond localStorage, no PII).
                "Consults held" counts consultations you mark as completed in the Consultations tab.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications">
          {applications.length === 0 ? (
            <Empty text="No partner applications yet." />
          ) : (
            <div className="space-y-3" data-testid="admin-applications-list">
              {applications.map((a) => (
                <div key={a.id} className="rounded-2xl border border-white/10 bg-surface p-6" data-testid={`admin-app-${a.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-display text-lg tracking-tight text-paper">{a.name}</p>
                        <StatusPill status={a.status} />
                      </div>
                      <p className="text-sm text-fog mt-1">{a.email}</p>
                      <p className="text-sm text-paper/90 mt-3"><span className="text-fog">Expertise:</span> {a.expertise}</p>
                      <p className="text-sm text-paper/90 mt-1"><span className="text-fog">Experience:</span> {a.years_experience} yrs{a.company ? ` · ${a.company}` : ""}</p>
                      {a.linkedin && <p className="text-sm text-fog mt-1 break-all">{a.linkedin}</p>}
                      {a.message && <p className="text-sm text-fog/80 mt-3 italic">"{a.message}"</p>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setStatus(a.id, "approved")}
                        disabled={a.status === "approved"}
                        data-testid={`approve-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-paper text-black text-sm font-medium px-4 py-2 hover:bg-white transition-colors disabled:opacity-40"
                      >
                        <Check size={14} /> Approve
                      </button>
                      <button
                        onClick={() => setStatus(a.id, "rejected")}
                        disabled={a.status === "rejected"}
                        data-testid={`reject-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-paper text-sm font-medium px-4 py-2 hover:bg-white/5 transition-colors disabled:opacity-40"
                      >
                        <X size={14} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="appointments">
          {appointments.length === 0 ? (
            <Empty text="No consultations booked yet." />
          ) : (
            <div className="space-y-3" data-testid="admin-appointments-list">
              {appointments.map((a) => (
                <div key={a.id} className="rounded-2xl border border-white/10 bg-surface p-6" data-testid={`admin-appt-${a.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <p className="font-display text-lg tracking-tight text-paper">{a.service}</p>
                        <StatusPill status={a.status} />
                      </div>
                      <p className="text-sm text-fog mt-1">{a.name} · {a.email}</p>
                      <div className="mt-3 flex items-center gap-4 text-sm text-fog">
                        <span className="flex items-center gap-1.5"><Calendar size={14} /> {a.date}</span>
                        <span className="flex items-center gap-1.5"><Clock size={14} /> {a.time_slot}</span>
                      </div>
                      {a.message && <p className="text-sm text-fog/80 mt-3">{a.message}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setApptStatus(a.id, "confirmed")}
                        disabled={a.status === "confirmed" || a.status === "cancelled"}
                        data-testid={`confirm-appt-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-paper text-black text-sm font-medium px-4 py-2 hover:bg-white transition-colors disabled:opacity-40"
                      >
                        <Check size={14} /> Confirm
                      </button>
                      <button
                        onClick={() => setApptStatus(a.id, "cancelled")}
                        disabled={a.status === "cancelled"}
                        data-testid={`cancel-appt-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-paper text-sm font-medium px-4 py-2 hover:bg-white/5 transition-colors disabled:opacity-40"
                      >
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages">
          {messages.length === 0 ? (
            <Empty text="No messages yet." />
          ) : (
            <div className="space-y-3" data-testid="admin-messages-list">
              {messages.map((m) => (
                <div key={m.id} className="rounded-2xl border border-white/10 bg-surface p-6" data-testid={`admin-msg-${m.id}`}>
                  <div className="flex items-center gap-2 text-paper flex-wrap">
                    <Mail size={15} />
                    <p className="font-display text-base tracking-tight">{m.name}</p>
                    <span className="text-sm text-fog">· {m.email}{m.company ? ` · ${m.company}` : ""}</span>
                    {m.triage?.category && (
                      <span className="ml-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/25 text-paper" data-testid={`msg-category-${m.id}`}>
                        {m.triage.category}
                      </span>
                    )}
                    {m.triage?.priority && (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                        m.triage.priority === "high" ? "text-red-400 border-red-400/40"
                        : m.triage.priority === "medium" ? "text-yellow-400 border-yellow-400/40"
                        : "text-fog border-white/20"}`}>
                        {m.triage.priority}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-fog/90 mt-3 leading-relaxed">{m.message}</p>
                  {m.triage?.suggested_reply && (
                    <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-widest text-fog">AI suggested reply</span>
                        <button
                          onClick={() => copyReply(m.id, m.triage.suggested_reply)}
                          data-testid={`msg-copy-reply-${m.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-paper text-xs font-medium px-3 py-1.5 hover:bg-white/5 transition-colors"
                        >
                          {copiedId === m.id ? <Check size={12} /> : <Copy size={12} />}
                          {copiedId === m.id ? "Copied" : "Copy reply"}
                        </button>
                      </div>
                      <p className="text-sm text-paper/90 leading-relaxed whitespace-pre-line">{m.triage.suggested_reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="booking">
          <form onSubmit={saveBooking} className="max-w-xl rounded-2xl border border-white/10 bg-surface p-8" data-testid="booking-settings-form">
            <h2 className="font-display text-xl tracking-tight text-paper mb-2">Booking calendar</h2>
            <p className="text-sm text-fog mb-6">
              Control when clients can book the free consultation and which time slots are offered.
              Current earliest bookable date: <span className="text-paper">{booking.min_date}</span>.
            </p>

            <label className="text-xs uppercase tracking-widest text-fog">Lead time (months from today)</label>
            <input
              type="number"
              min={0}
              max={24}
              value={booking.lead_time_months}
              onChange={(e) => setBooking((b) => ({ ...b, lead_time_months: e.target.value }))}
              data-testid="booking-lead-months"
              className="mt-2 mb-5 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
            />

            <label className="text-xs uppercase tracking-widest text-fog">Available time slots (comma-separated)</label>
            <input
              value={slotsText}
              onChange={(e) => setSlotsText(e.target.value)}
              placeholder="09:00, 10:30, 12:00, 13:30, 15:00, 16:30"
              data-testid="booking-slots"
              className="mt-2 mb-6 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors placeholder:text-fog/50"
            />

            <button
              type="submit"
              disabled={savingBooking}
              data-testid="booking-settings-save"
              className="inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-6 py-3 hover:bg-white transition-colors disabled:opacity-60"
            >
              <Save size={15} /> {savingBooking ? "Saving…" : "Save calendar settings"}
            </button>
          </form>
        </TabsContent>

        <TabsContent value="reservations">
          <div data-testid="reservations-panel" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
              {[
                { key: "barecord", label: "BArecord reservations", data: reservations?.barecord },
                { key: "securedesk", label: "SecureDesk reservations", data: reservations?.securedesk },
              ].map((p) => (
                <div key={p.key} className="bg-surface p-7">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-4xl tracking-tighter text-paper" data-testid={`reservations-count-${p.key}`}>
                        {p.data?.count ?? 0}
                      </div>
                      <div className="mt-2 text-xs uppercase tracking-widest text-fog">{p.label}</div>
                    </div>
                    <button
                      onClick={() => exportReservations(p.key)}
                      data-testid={`reservations-export-${p.key}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-paper text-xs font-medium px-3.5 py-2 hover:bg-white/5 transition-colors"
                    >
                      <Download size={13} /> CSV
                    </button>
                  </div>
                  <div className="mt-5 space-y-2 max-h-64 overflow-auto">
                    {(p.data?.recent || []).length === 0 ? (
                      <p className="text-xs text-fog/70">No sign-ups yet.</p>
                    ) : (
                      p.data.recent.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
                          <span className="text-paper truncate mr-3">{r.email}</span>
                          <span className="text-fog/60 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 bg-surface p-7" data-testid="launch-announce-control">
              <h3 className="font-display text-lg tracking-tight text-paper">Launch-day announcement</h3>
              <p className="mt-2 text-sm text-fog leading-relaxed">
                A "we're live" email is sent <span className="text-paper">automatically on November 7, 2026</span> to every
                BArecord and SecureDesk reserver. Use the button below only if you want to send it manually / early.
              </p>
              {reservations?.announced ? (
                <p className="mt-4 text-xs text-green-400" data-testid="launch-announced-status">
                  Already sent{reservations.announced_at ? ` on ${reservations.announced_at.slice(0, 10)}` : ""}.
                </p>
              ) : (
                <p className="mt-4 text-xs text-fog/70" data-testid="launch-announced-status">Not sent yet.</p>
              )}
              <button
                onClick={sendAnnouncement}
                disabled={announcing}
                data-testid="launch-announce-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-6 py-3 hover:bg-white transition-colors disabled:opacity-60"
              >
                <Mail size={15} /> {announcing ? "Sending…" : "Send launch email now"}
              </button>
              <button
                onClick={sendDigest}
                data-testid="send-digest-btn"
                className="mt-5 ml-3 inline-flex items-center gap-2 rounded-full border border-white/20 text-paper text-sm font-medium px-6 py-3 hover:bg-white/5 transition-colors"
              >
                <Mail size={15} /> Email me the weekly digest
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-surface p-7" data-testid="drip-control">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-lg tracking-tight text-paper">Pre-launch warm-up drip</h3>
                  <p className="mt-2 text-sm text-fog leading-relaxed max-w-xl">
                    An automatic email sequence keeps reservers warm on the way to Nov 7. Each step sends once,
                    automatically, on its date — no action needed.
                  </p>
                </div>
                <button
                  onClick={() => toggleDrip(!(drip?.enabled))}
                  data-testid="drip-toggle"
                  className={`shrink-0 rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                    drip?.enabled ? "border-green-400/40 text-green-400" : "border-white/20 text-fog hover:bg-white/5"
                  }`}
                >
                  {drip?.enabled ? "Enabled" : "Paused"}
                </button>
              </div>
              <div className="mt-5 space-y-6">
                {(drip?.tracks || []).map((track) => (
                  <div key={track.key} data-testid={`drip-track-${track.key}`}>
                    <p className="text-xs uppercase tracking-widest text-paper mb-2">{track.label} sequence</p>
                    <div className="space-y-2">
                      {(track.steps || []).map((s) => (
                        <div key={s.key} className="flex items-center justify-between text-sm border-b border-white/5 pb-2" data-testid={`drip-step-${track.key}-${s.key}`}>
                          <div className="min-w-0">
                            <p className="text-paper truncate">{s.subject}</p>
                            <p className="text-xs text-fog/60">{(s.send_at || "").slice(0, 10)}</p>
                          </div>
                          <span className="text-xs text-fog whitespace-nowrap ml-3">{s.sent} sent</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="launch">
          <div className="max-w-xl rounded-2xl border border-white/10 bg-surface p-8" data-testid="launch-control">
            <h3 className="font-display text-xl tracking-tight text-paper">BArecord &amp; SecureDesk launch access</h3>
            <p className="mt-2 text-sm text-fog leading-relaxed">
              Sign-ups are always open. This controls whether registered users can access the
              BArecord dashboard and SecureDesk. Default unlock date: <span className="text-paper">November 7, 2026</span>.
            </p>

            {launch && (
              <div className="mt-6 flex items-center gap-3 text-sm">
                <span className="text-fog">Current status:</span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs uppercase tracking-widest border ${
                    launch.locked ? "text-yellow-400 border-yellow-400/40" : "text-green-400 border-green-400/40"
                  }`}
                  data-testid="launch-current-status"
                >
                  {launch.locked ? "Locked (early access)" : "Open"}
                </span>
                <span className="text-fog/60 text-xs">mode: {launch.override}</span>
              </div>
            )}

            <div className="mt-7 grid gap-3">
              {[
                { key: "auto", label: "Automatic", desc: "Locked until Nov 7, 2026, then opens automatically." },
                { key: "open", label: "Force open", desc: "Open access now, before the launch date." },
                { key: "locked", label: "Force locked", desc: "Keep access locked regardless of date." },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => updateLaunch(opt.key)}
                  data-testid={`launch-set-${opt.key}`}
                  className={`text-left rounded-xl border p-4 transition-colors ${
                    launch?.override === opt.key
                      ? "border-paper bg-white/5"
                      : "border-white/10 hover:bg-[#232326]"
                  }`}
                >
                  <p className="text-sm text-paper font-medium">{opt.label}</p>
                  <p className="text-xs text-fog mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: "text-green-400 border-green-400/40",
    rejected: "text-red-400 border-red-400/40",
    cancelled: "text-red-400 border-red-400/40",
    pending: "text-yellow-400 border-yellow-400/40",
    scheduled: "text-paper border-white/30",
    confirmed: "text-green-400 border-green-400/40",
    completed: "text-blue-400 border-blue-400/40",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${map[status] || "text-fog border-white/20"}`}>
      {status}
    </span>
  );
}

function Empty({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-sm text-fog">
      {text}
    </div>
  );
}
